import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { Input } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import type { tTagFilter } from 'src/pages/Pipe/Pipe.types.ts';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context.ts';
import {
  getTagCatalogRevision,
  getWorkspaceTags,
  subscribeToTagCatalog,
} from '../../ChatInfoAttributes.tag.catalog.ts';

type tTagTypeaheadProps = {
  assignedTags: string[];
  inputRef?: Ref<HTMLInputElement>;
  onChange: (value: string) => void;
  // Fired only when an option is picked, never while typing: the parent
  // attaches the tag on the pick, so it must not react to every keystroke.
  onSelect?: (option: tTagOption) => void;
  value: string;
};

export type tTagOption = {
  label: string;
  value: string;
  // True for the "create «X»" option, so the parent can report whether the
  // pick created a tag or reused one from the workspace catalog.
  isNew: boolean;
};

type tCatalogStatus = 'idle' | 'loading' | 'ready' | 'failed';

// No option is highlighted until the user moves to one. Preselecting the first
// partial match made Enter attach that tag instead of creating the literal text
// the user typed, and a tag that is a prefix of a longer one became
// uncreatable (BUG-938).
const NO_ACTIVE_INDEX = -1;

// The panel is a daisyUI collapse, and `.collapse` carries `overflow: hidden`
// to drive the accordion animation. An absolutely positioned child cannot
// escape that, and no z-index helps because the list is clipped, not stacked
// below. The list is portaled to the body and placed from the input rect
// instead, which leaves the accordion animation untouched (BUG-938).
const LIST_MAX_HEIGHT = 160;
// A box that scrolls its own overflow and is handed a max-height of 0 is
// present and correctly placed but paints nothing. Below this floor the list
// would be unreadable anyway, so it keeps its height and overflows instead.
const LIST_MIN_HEIGHT = 96;
// A focused empty field offers the workspace catalog, so a tag can be picked
// without being typed out. That catalog can hold hundreds of tags while the
// list shows a handful at a time behind LIST_MAX_HEIGHT, so what renders is
// capped: plenty to scroll, far short of pouring the whole catalog into the
// DOM on every focus. Typing past this narrows the list anyway (BUG-939).
const MAX_EMPTY_QUERY_OPTIONS = 50;
// Fuzzy suggestions start here. Matching a whole workspace catalog against a
// single character produces noise, so below this floor the query resolves to at
// most one option instead (BUG-932).
const MIN_SUGGESTION_LENGTH = 2;
const LIST_OFFSET = 4;
const VIEWPORT_MARGIN = 8;

type tListPosition = {
  left: number;
  width: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
};

const normalizeTag = (tag: string): string => tag.trim().toLowerCase();

export default function TagTypeahead(props: tTagTypeaheadProps): JSX.Element {
  const { currentWorkspaceId } = useWorkspaceContext();
  const { t } = useTranslation(['chat/chat']);
  const listboxId = useId();
  const [catalog, setCatalog] = useState<tTagFilter[]>([]);
  const [catalogWorkspaceId, setCatalogWorkspaceId] = useState<string>();
  const [catalogStatus, setCatalogStatus] = useState<tCatalogStatus>(
    currentWorkspaceId ? 'loading' : 'idle',
  );
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(NO_ACTIVE_INDEX);
  const [listPosition, setListPosition] = useState<tListPosition>();
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Re-reads the catalog whenever an attach or a delete invalidates it, so a
  // panel that stays open across a tag change stops deciding against the
  // snapshot it loaded on mount (BUG-940).
  const catalogRevision = useSyncExternalStore(
    subscribeToTagCatalog,
    getTagCatalogRevision,
  );

  useEffect(() => {
    if (!currentWorkspaceId) {
      setCatalog([]);
      setCatalogWorkspaceId(undefined);
      setCatalogStatus('idle');
      return;
    }

    let mounted = true;
    setCatalogStatus('loading');
    getWorkspaceTags(currentWorkspaceId).then(
      (tags) => {
        if (mounted) {
          setCatalog(tags);
          setCatalogWorkspaceId(currentWorkspaceId);
          setCatalogStatus('ready');
        }
      },
      () => {
        if (mounted) {
          setCatalog([]);
          setCatalogWorkspaceId(undefined);
          setCatalogStatus('failed');
        }
      },
    );

    return () => {
      mounted = false;
    };
  }, [catalogRevision, currentWorkspaceId]);

  const isCatalogAvailable =
    catalogStatus === 'ready' && catalogWorkspaceId === currentWorkspaceId;
  const options = useMemo((): tTagOption[] => {
    const query = normalizeTag(props.value);
    if (!isCatalogAvailable) return [];

    const assigned = new Set(props.assignedTags.map(normalizeTag));

    // An empty query has nothing to match and nothing to create, so it simply
    // offers the workspace catalog minus the tags the lead already carries.
    // This is what the browser's own autocomplete used to do before it was
    // turned off, and what the customer report asks for back (BUG-939).
    if (!query)
      return catalog
        .filter((tag) => !assigned.has(normalizeTag(tag.name)))
        .slice(0, MAX_EMPTY_QUERY_OPTIONS)
        .map((tag) => ({ label: tag.name, value: tag.name, isNew: false }));

    const exactCatalogTag = catalog.find(
      (tag) => normalizeTag(tag.name) === query,
    );
    const createOption: tTagOption = {
      label: t('attributes.createTag', { tag: props.value.trim() }),
      value: props.value.trim(),
      isNew: true,
    };

    // A single character offers at most one option: the tag it would create,
    // or the existing tag it already names — otherwise that tag is unreachable,
    // uncreatable because it exists and invisible because there is no list.
    if (query.length < MIN_SUGGESTION_LENGTH) {
      if (assigned.has(query)) return [];
      if (!exactCatalogTag) return [createOption];

      return [
        {
          label: exactCatalogTag.name,
          value: exactCatalogTag.name,
          isNew: false,
        },
      ];
    }

    const matchingTags = catalog
      .filter((tag) => {
        const normalizedName = normalizeTag(tag.name);
        return normalizedName.includes(query) && !assigned.has(normalizedName);
      })
      .map((tag) => ({ label: tag.name, value: tag.name, isNew: false }));

    if (exactCatalogTag || assigned.has(query)) return matchingTags;

    return [...matchingTags, createOption];
  }, [catalog, isCatalogAvailable, props.assignedTags, props.value, t]);

  const visibleOptions = isOpen && options.length > 0;

  useEffect(() => {
    setActiveIndex(NO_ACTIVE_INDEX);
  }, [props.value]);

  const updateListPosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const roomBelow =
      viewportHeight - rect.bottom - LIST_OFFSET - VIEWPORT_MARGIN;
    const roomAbove = rect.top - LIST_OFFSET - VIEWPORT_MARGIN;
    const opensUpward = roomBelow < LIST_MAX_HEIGHT && roomAbove > roomBelow;
    const room = opensUpward ? roomAbove : roomBelow;

    setListPosition({
      left: rect.left,
      width: rect.width,
      maxHeight: Math.min(LIST_MAX_HEIGHT, Math.max(LIST_MIN_HEIGHT, room)),
      ...(opensUpward
        ? { bottom: viewportHeight - rect.top + LIST_OFFSET }
        : { top: rect.bottom + LIST_OFFSET }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!visibleOptions) {
      setListPosition(undefined);
      return;
    }

    updateListPosition();
    // Capturing, so the scroll of any nested container between the input and
    // the window moves the list with it, not just the window itself.
    window.addEventListener('scroll', updateListPosition, true);
    window.addEventListener('resize', updateListPosition);

    return () => {
      window.removeEventListener('scroll', updateListPosition, true);
      window.removeEventListener('resize', updateListPosition);
    };
  }, [updateListPosition, visibleOptions]);

  const selectOption = (option: tTagOption) => {
    props.onChange(option.value);
    props.onSelect?.(option);
    setIsOpen(false);
    anchorRef.current?.querySelector('input')?.blur();
  };

  // The list lives in a portal, so it is no longer a DOM descendant of the
  // container: `Node.contains` alone would miss it and close the list before a
  // click on an option could land. Both roots are checked instead.
  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (
      nextTarget &&
      (event.currentTarget.contains(nextTarget) ||
        listRef.current?.contains(nextTarget))
    )
      return;

    setIsOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }

    if (!options.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((previousIndex) => {
        const isForward = event.key === 'ArrowDown';
        if (previousIndex === NO_ACTIVE_INDEX)
          return isForward ? 0 : options.length - 1;

        const direction = isForward ? 1 : -1;
        return (previousIndex + direction + options.length) % options.length;
      });
      return;
    }

    if (event.key === 'Enter' && visibleOptions) {
      // Keep the enclosing form from submitting while the listbox is open,
      // whether or not Enter resolves to an option.
      event.preventDefault();
      if (activeIndex !== NO_ACTIVE_INDEX) {
        selectOption(options[activeIndex]);
        return;
      }

      // Nothing highlighted: Enter commits exactly what was typed. The create
      // option is that literal text; when there is none the query already names
      // an existing tag, so only an exact match may be picked.
      const query = normalizeTag(props.value);
      const literalOption =
        options.find((option) => option.isNew) ??
        options.find((option) => normalizeTag(option.value) === query);
      if (literalOption) selectOption(literalOption);
    }
  };

  const listStyle: CSSProperties | undefined = listPosition && {
    position: 'fixed',
    left: `${listPosition.left}px`,
    width: `${listPosition.width}px`,
    maxHeight: `${listPosition.maxHeight}px`,
    ...(listPosition.top === undefined
      ? { bottom: `${listPosition.bottom}px` }
      : { top: `${listPosition.top}px` }),
  };

  return (
    <div
      ref={anchorRef}
      className="ChatInfoAttributes__Content__Tags__Typeahead"
      onBlur={handleBlur}
    >
      <Input
        ref={props.inputRef}
        color="ghost"
        size="sm"
        name="tag"
        maxLength={128}
        type="text"
        spellCheck={false}
        autoComplete="off"
        autoCorrect="off"
        role="combobox"
        aria-label={t('attributes.tags')}
        aria-autocomplete="list"
        aria-controls={visibleOptions ? listboxId : undefined}
        aria-expanded={visibleOptions}
        aria-activedescendant={
          visibleOptions && activeIndex !== NO_ACTIVE_INDEX
            ? `${listboxId}-option-${activeIndex}`
            : undefined
        }
        value={props.value}
        onChange={(event) => {
          props.onChange(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={t('attributes.tagLabel')}
        className="ChatInfoAttributes__Content__Item__Value w-full"
      />
      {visibleOptions && listStyle
        ? createPortal(
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-label={t('attributes.tagSuggestions')}
              className="ChatInfoAttributes__Content__Tags__Options"
              // eslint-disable-next-line no-restricted-syntax -- the anchor rect is only known at runtime; no utility class can express it
              style={listStyle}
              onBlur={handleBlur}
            >
              {options.map((option, index) => (
                <li key={`${option.value}-${index}`} role="presentation">
                  <button
                    id={`${listboxId}-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    className="ChatInfoAttributes__Content__Tags__Option"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => selectOption(option)}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
